/**
 * Sprint 20 — cross-project cron-tasks admin dashboard.
 *
 * Three tabs:
 *   - Running now — cron_task_runs with status='running', [Cancel] per row.
 *     Not real-time: everything loads over REST (CronTasksAdminApiClient) on open,
 *     on a task-type filter change, after Cancel / pause / resume, and on the
 *     Refresh button. No onSnapshot, no polling.
 *   - All definitions — cross-project flat list, filterable by taskType.
 *   - Tick history — recent cron_task_ticks entries.
 *
 * Replaces the Sprint 19 SchedulersAdminPage.
 *
 * Auth: client-side gate by `useAuth().isAdmin`; backend `requireAdmin`
 * middleware is the real enforcement layer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CancelIcon from '@mui/icons-material/Cancel';
import PauseIcon from '@mui/icons-material/PauseCircleOutline';
import PlayIcon from '@mui/icons-material/PlayCircleOutline';
import { useAuth } from '../../contexts/AuthContext';
import { getAuth } from 'firebase/auth';
import {
  CronTasksAdminApiClient,
  type CronTaskDefinitionDTO,
  type CronTaskRunDTO,
  type CronTaskTickDTO,
  type KillSwitchStateDTO,
} from '@modules/content-plan/services/CronTasksAdminApiClient';

const T = {
  PAGE: 'cron-tasks-admin-page',
  TAB_RUNNING: 'cron-tasks-admin-tab-running',
  TAB_DEFS: 'cron-tasks-admin-tab-definitions',
  TAB_TICKS: 'cron-tasks-admin-tab-ticks',
  REFRESH: 'cron-tasks-admin-refresh',
  KILL_CHIP: 'cron-tasks-admin-kill-chip',
  KILL_TOGGLE: 'cron-tasks-admin-kill-toggle',
  TYPE_FILTER: 'cron-tasks-admin-type-filter',
  RUN_ROW: (id: string) => `cron-tasks-admin-run-${id}`,
  DEF_ROW: (id: string) => `cron-tasks-admin-def-${id}`,
  TICK_ROW: (id: string) => `cron-tasks-admin-tick-${id}`,
  KILL_BTN: (id: string) => `cron-tasks-admin-kill-${id}`,
};

export default function CronTasksAdminPage(): React.ReactElement {
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<'running' | 'definitions' | 'ticks'>('running');
  const [runs, setRuns] = useState<CronTaskRunDTO[]>([]);
  const [definitions, setDefinitions] = useState<CronTaskDefinitionDTO[]>([]);
  const [ticks, setTicks] = useState<CronTaskTickDTO[]>([]);
  const [countsByStatus, setCountsByStatus] = useState<Record<string, number> | null>(null);
  const [countsByType, setCountsByType] = useState<Record<string, number> | null>(null);
  const [killSwitch, setKillSwitch] = useState<KillSwitchStateDTO | null>(null);
  const [knownTypes, setKnownTypes] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKill, setBusyKill] = useState(false);

  const client = useMemo(
    () =>
      new CronTasksAdminApiClient({
        getAuthToken: async () => {
          const user = getAuth().currentUser;
          return user ? user.getIdToken() : null;
        },
      }),
    [],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [status, running, defs, ticksResp] = await Promise.all([
        client.status(),
        client.listRunning(),
        client.listDefinitions({ taskType: typeFilter || undefined }),
        client.listTicks(100),
      ]);
      if (!status.success) throw new Error(status.error ?? 'status failed');
      if (!running.success) throw new Error(running.error ?? 'running failed');
      if (!defs.success) throw new Error(defs.error ?? 'definitions failed');
      if (!ticksResp.success) throw new Error(ticksResp.error ?? 'ticks failed');
      setCountsByStatus(status.countsByStatus ?? null);
      setCountsByType(status.countsByType ?? null);
      setKillSwitch(status.killSwitch ?? null);
      setKnownTypes(status.knownTaskTypes ?? []);
      setRuns(running.runs ?? []);
      setDefinitions(defs.definitions ?? []);
      setTicks(ticksResp.ticks ?? []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [client, typeFilter]);

  useEffect(() => {
    if (isAdmin) refresh();
  }, [isAdmin, refresh]);

  const handleKillRun = async (runId: string) => {
    if (!window.confirm(`Cancel run ${runId}? Executor will stop at the next phase boundary.`)) return;
    try {
      const res = await client.cancelRun(runId);
      if (!res.success) throw new Error(res.error ?? 'cancel failed');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const handleToggleKillSwitch = async () => {
    setBusyKill(true);
    setError(null);
    try {
      const isOn = !!killSwitch?.enabled;
      const res = isOn
        ? await client.globalResume()
        : await client.globalPause(60, 'admin UI toggle');
      if (!res.success) throw new Error(res.error ?? 'kill-switch toggle failed');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyKill(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Admin access required.</Alert>
      </Box>
    );
  }

  const killEnabled = !!killSwitch?.enabled;

  return (
    <Box data-testid={T.PAGE} sx={{ p: 3, maxWidth: 1280 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          Cron tasks admin
        </Typography>
        <Tooltip title={killEnabled ? 'Tick is currently short-circuited' : 'Tick runs normally'}>
          <Chip
            data-testid={T.KILL_CHIP}
            size="small"
            color={killEnabled ? 'error' : 'default'}
            icon={killEnabled ? <PauseIcon /> : <PlayIcon />}
            label={
              killEnabled
                ? `KILL-SWITCH ON${killSwitch?.enabledUntil ? ` · until ${new Date(killSwitch.enabledUntil).toLocaleTimeString()}` : ''}`
                : 'Kill-switch OFF'
            }
          />
        </Tooltip>
        <Button
          variant={killEnabled ? 'contained' : 'outlined'}
          color={killEnabled ? 'success' : 'error'}
          size="small"
          onClick={handleToggleKillSwitch}
          disabled={busyKill}
          data-testid={T.KILL_TOGGLE}
          startIcon={killEnabled ? <PlayIcon /> : <PauseIcon />}
        >
          {killEnabled ? 'Resume scheduler' : 'Pause all ticks (60 min)'}
        </Button>
        <IconButton
          onClick={refresh}
          disabled={loading}
          aria-label="refresh"
          data-testid={T.REFRESH}
        >
          <RefreshIcon />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Chip size="small" label={`Active: ${countsByStatus?.active ?? 0}`} color="success" />
        <Chip size="small" label={`Paused: ${countsByStatus?.paused ?? 0}`} color="warning" />
        <Chip size="small" label={`Archived: ${countsByStatus?.archived ?? 0}`} />
        <Chip
          size="small"
          color={runs.length > 0 ? 'info' : 'default'}
          label={`Running now: ${runs.length}`}
        />
        {countsByType &&
          Object.entries(countsByType).map(([t, n]) => (
            <Chip key={t} size="small" variant="outlined" label={`${t}: ${n}`} />
          ))}
        {ticks[0] && (
          <Chip
            size="small"
            variant="outlined"
            label={`Last tick: ${timeAgo(ticks[0].startedAt)}`}
          />
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Running now (${runs.length})`} value="running" data-testid={T.TAB_RUNNING} />
        <Tab
          label={`All definitions (${definitions.length})`}
          value="definitions"
          data-testid={T.TAB_DEFS}
        />
        <Tab label={`Tick history (${ticks.length})`} value="ticks" data-testid={T.TAB_TICKS} />
      </Tabs>

      {tab === 'running' && <RunningTable runs={runs} onKill={handleKillRun} loading={loading} />}

      {tab === 'definitions' && (
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Filter by taskType:
            </Typography>
            <TextField
              size="small"
              select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              SelectProps={{ native: true }}
              inputProps={{ 'data-testid': T.TYPE_FILTER }}
              sx={{ minWidth: 200 }}
            >
              <option value="">All task types</option>
              {knownTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </TextField>
          </Stack>
          <DefinitionsTable definitions={definitions} loading={loading} />
        </Box>
      )}

      {tab === 'ticks' && <TicksList ticks={ticks} loading={loading} />}
    </Box>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

const STATUS_COLOR: Record<
  CronTaskDefinitionDTO['status'],
  'success' | 'warning' | 'default'
> = { active: 'success', paused: 'warning', archived: 'default' };

const RunningTable: React.FC<{
  runs: CronTaskRunDTO[];
  onKill: (runId: string) => void;
  loading: boolean;
}> = ({ runs, onKill, loading }) => {
  if (loading && runs.length === 0) return <Alert severity="info">Loading…</Alert>;
  if (runs.length === 0) return <Alert severity="success">Nothing running at the moment.</Alert>;
  return (
    <TableContainer sx={{ borderRadius: 1, bgcolor: 'action.hover' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Project</TableCell>
            <TableCell>Task type</TableCell>
            <TableCell>Run ID</TableCell>
            <TableCell>Attempt</TableCell>
            <TableCell>Active phase</TableCell>
            <TableCell>Started</TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {runs.map((r) => {
            const activePhase = Object.entries(r.phases ?? {}).find(
              ([, v]) => v.status === 'running',
            )?.[0];
            return (
              <TableRow key={r.id} data-testid={T.RUN_ROW(r.id)} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {r.projectId ?? '—'}
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={r.taskType} />
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{r.id}</TableCell>
                <TableCell>{r.attempts}</TableCell>
                <TableCell>{activePhase ? <Chip size="small" label={activePhase} /> : '—'}</TableCell>
                <TableCell>{timeAgo(r.startedAt)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Cancel this run">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onKill(r.id)}
                      data-testid={T.KILL_BTN(r.id)}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const DefinitionsTable: React.FC<{
  definitions: CronTaskDefinitionDTO[];
  loading: boolean;
}> = ({ definitions, loading }) => {
  if (loading && definitions.length === 0) return <Alert severity="info">Loading…</Alert>;
  if (definitions.length === 0) return <Alert severity="info">No definitions.</Alert>;
  return (
    <TableContainer sx={{ borderRadius: 1, bgcolor: 'action.hover' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Project</TableCell>
            <TableCell>Task type</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Cadence</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Next run</TableCell>
            <TableCell>Last run</TableCell>
            <TableCell align="right">Fired</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {definitions.map((d) => (
            <TableRow key={d.id} data-testid={T.DEF_ROW(d.id)} hover>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{d.projectId ?? '—'}</TableCell>
              <TableCell>
                <Chip size="small" variant="outlined" label={d.taskType} />
              </TableCell>
              <TableCell>{d.name}</TableCell>
              <TableCell>
                {d.schedule.preset ?? (d.schedule.intervalMinutes ? `${d.schedule.intervalMinutes}m` : '—')}
              </TableCell>
              <TableCell>
                <Chip size="small" label={d.status} color={STATUS_COLOR[d.status]} />
              </TableCell>
              <TableCell>{timeAgo(d.schedule.nextRunAt)}</TableCell>
              <TableCell>{d.schedule.lastRunAt ? timeAgo(d.schedule.lastRunAt) : '—'}</TableCell>
              <TableCell align="right">{d.runsFired}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TicksList: React.FC<{ ticks: CronTaskTickDTO[]; loading: boolean }> = ({ ticks, loading }) => {
  if (loading && ticks.length === 0) return <Alert severity="info">Loading…</Alert>;
  if (ticks.length === 0) return <Alert severity="info">No tick history yet.</Alert>;
  return (
    <Stack spacing={0.5} sx={{ fontFamily: 'monospace', fontSize: 13 }}>
      {ticks.map((t) => (
        <Box
          key={t.id}
          data-testid={T.TICK_ROW(t.id)}
          sx={{
            p: 1,
            borderLeft: '2px solid',
            borderColor: t.failures?.length ? 'error.main' : t.skipped ? 'warning.main' : 'primary.main',
            bgcolor: 'action.hover',
            borderRadius: 0.5,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Chip size="small" variant="outlined" label={t.triggeredBy} />
            <Chip size="small" variant="outlined" label={`role: ${t.role ?? '—'}`} />
            <Typography variant="caption">{new Date(t.startedAt).toLocaleString()}</Typography>
            {t.skipped && <Chip size="small" color="warning" label={`skipped · ${t.reason ?? ''}`} />}
            <Chip size="small" variant="outlined" label={`considered: ${t.tasksConsidered}`} />
            {t.tasksSkippedRemote ? (
              <Chip size="small" variant="outlined" label={`skipped remote: ${t.tasksSkippedRemote}`} />
            ) : null}
            <Chip size="small" variant="outlined" label={`fired: ${t.runsCreated}`} />
            <Chip size="small" variant="outlined" label={`zombies: ${t.zombiesSwept}`} />
            {t.failures?.length ? (
              <Chip size="small" color="error" label={`failures: ${t.failures.length}`} />
            ) : null}
            <Chip size="small" variant="outlined" label={`${t.elapsedMs}ms`} />
            {Object.entries(t.failuresByType ?? {}).map(([k, v]) =>
              v > 0 ? <Chip key={k} size="small" color="warning" label={`${k}: ${v}`} /> : null,
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

function timeAgo(iso: string | undefined): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const diff = ms - Date.now();
  const abs = Math.abs(diff);
  const mins = Math.round(abs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${diff < 0 ? '' : 'in '}${mins}m${diff < 0 ? ' ago' : ''}`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${diff < 0 ? '' : 'in '}${hours}h${diff < 0 ? ' ago' : ''}`;
  const days = Math.round(hours / 24);
  return `${diff < 0 ? '' : 'in '}${days}d${diff < 0 ? ' ago' : ''}`;
}
